import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { useBuilderUsage } from '@ctw/shared/content/summarization/use-builder-usage';
import {
  type PatientSummaryStatus,
  type UsePatientSummariesResponse,
  usePatientSummaries,
} from '@ctw/shared/content/summarization/use-patient-summaries';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { TIMER_PATIENT_SUMMARY_GENERATION_DURATION_IN_SECONDS } from '@ctw/shared/context/telemetry/timer-metrics';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { faExternalLink } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const SUMMARY_LIMIT_APPROACHING_THRESHOLD = 0.8 as const;

export interface ActiveSummaryJob {
  patientUpid: string;
  status: PatientSummaryStatus;
}

interface UserPatientSummarizationState {
  summariesAllotted: number;
  summariesUsed: number;
  summarizationLimitApproaching: boolean;
  summarizationLimitReached: boolean;
  showMetering: boolean;

  reloadUsage: () => Promise<void>;

  activeSummaryJob: ActiveSummaryJob | null;
  summaryResponse: UsePatientSummariesResponse;
  generateSummary: (patientUpid: string) => void;
  checkCachedSummary: (patientUpid: string) => Promise<void>;
  /**
   * Delete the cached summary job for the active patient. Internal Zus-user only — used to
   * bust the server-side cache and force a fresh generation. Returns true on success.
   */
  deleteCachedSummary: () => Promise<boolean>;
  registerSummaryPageMount: () => () => void;
}

const UserPatientSummarizationContext = createContext<UserPatientSummarizationState | undefined>(
  undefined,
);

export const UserPatientSummarizationProvider = ({ children }: PropsWithChildren) => {
  const { navigate, pathname } = useZuiContext();
  const telemetry = useTelemetry();
  const { requestContext } = useCTW();
  const { monthlyLimit, summariesUsed, billingTier, isLoading, refetch } = useBuilderUsage();
  const hasMeteringFeature = useFeatureFlag('summarization-free-usage-limit');
  const summaryResult = usePatientSummaries({ autoSubmit: false });

  const [activePatientUpid, setActivePatientUpid] = useState<string | null>(null);
  const prevStatusRef = useRef(summaryResult.status);
  const prevHasContentRef = useRef(summaryResult.summaries.length > 0);
  // Counter rather than a boolean so that overlapping mounts don't accidentally clear the flag
  const summaryPageMountCountRef = useRef(0);

  const registerSummaryPageMount = useCallback(() => {
    summaryPageMountCountRef.current += 1;
    return () => {
      summaryPageMountCountRef.current = Math.max(0, summaryPageMountCountRef.current - 1);
    };
  }, []);

  const generateSummary = useCallback(
    (patientUpid: string) => {
      setActivePatientUpid(patientUpid);
      summaryResult.submitJob(patientUpid);
    },
    [summaryResult.submitJob],
  );

  const deleteCachedSummary = useCallback(async (): Promise<boolean> => {
    const jobId = summaryResult.summaryJobId;
    if (!jobId) {
      return false;
    }
    const success = await summaryResult.deleteSummaryJob(jobId);
    if (success) {
      // Clear the active patient so consumers re-render in the pre-submission state.
      setActivePatientUpid(null);
      prevStatusRef.current = 'running';
      notify({ type: 'success', title: 'Cached summary deleted' });
    } else {
      notify({ type: 'error', title: 'Unable to delete cached summary' });
    }
    return success;
  }, [summaryResult.deleteSummaryJob, summaryResult.summaryJobId]);

  const checkCachedSummary = useCallback(
    async (patientUpid: string) => {
      // Always update the active patient — this clears any prior job for a different patient.
      setActivePatientUpid(patientUpid);

      const cachedStatus = await summaryResult.checkCachedSummary(patientUpid);
      if (cachedStatus === 'completed') {
        prevStatusRef.current = 'completed';
        prevHasContentRef.current = true;
      } else if (!cachedStatus) {
        // No cached job found — clear the active patient so the card shows the generate button.
        setActivePatientUpid(null);
      }
    },
    [summaryResult.checkCachedSummary],
  );

  // In the standalone app, stop polling when navigating away from the patient's pages.
  // Skipped in iframe contexts where the UPID is not in the URL (and you can't navigate away from the patient anyway).
  useEffect(() => {
    if (!requestContext.isInIframe && activePatientUpid && !pathname.includes(activePatientUpid)) {
      setActivePatientUpid(null);
      summaryResult.reset();
    }
  }, [requestContext.isInIframe, activePatientUpid, pathname, summaryResult.reset]);

  // Handle job completion and error notifications
  useEffect(() => {
    if (!activePatientUpid) {
      return;
    }

    const hasContent = summaryResult.summaries.length > 0;
    const firstContentArrived = !prevHasContentRef.current && hasContent;

    if (firstContentArrived) {
      void refetch().then(() => {
        const durationWaitedInSeconds = telemetry.stopTimerMetric(
          'loading',
          TIMER_PATIENT_SUMMARY_GENERATION_DURATION_IN_SECONDS,
        );

        if (summaryPageMountCountRef.current > 0) {
          // Suppress the "ready to view" toast when the user is already viewing the summary
          return;
        }

        telemetry.trackInteraction('patient_summary_ready_toast_presented', {
          summaryJobId: summaryResult.summaryJobId,
          durationWaitedInSeconds,
        });

        notify({
          type: 'success',
          title: 'Your patient summary is ready to view!',
          body: (
            <div className={tw`flex items-center justify-start gap-3`}>
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate('summary')}
                telemetryInteractionName="patient_summary_ready_toast_view_button_clicked"
                telemetryObfuscatedIdentifier="View Patient Summary"
                telemetryContext={{
                  summaryJobId: summaryResult.summaryJobId,
                }}
              >
                <div className={tw`flex items-center space-x-2`}>
                  <div className={tw`whitespace-nowrap text-sm`}>View Patient Summary</div>
                  <FontAwesomeIcon icon={faExternalLink} className={tw`h-4 w-4`} />
                </div>
              </Button>
            </div>
          ),
        });
      });
    }

    if (prevStatusRef.current === 'running' && summaryResult.status === 'error') {
      notify({ type: 'error', title: 'Unable to generate patient summary' });
      setActivePatientUpid(null);
    }

    if (prevStatusRef.current === 'running' && summaryResult.status === 'insufficient-data') {
      notify({
        type: 'info',
        title: "Patient data isn't ready yet",
        body: 'The patient history job may still be running — check back shortly.',
      });
      setActivePatientUpid(null);
    }

    prevStatusRef.current = summaryResult.status;
    prevHasContentRef.current = hasContent;
  }, [
    activePatientUpid,
    summaryResult.status,
    summaryResult.summaryJobId,
    summaryResult.summaries.length,
    refetch,
    navigate,
    telemetry,
  ]);

  const activeSummaryJob: ActiveSummaryJob | null = activePatientUpid
    ? { patientUpid: activePatientUpid, status: summaryResult.status }
    : null;

  const summarizationState = useMemo(() => {
    const summarizationLimitReached = summariesUsed >= monthlyLimit && monthlyLimit > 0;
    const summarizationLimitApproaching =
      !summarizationLimitReached &&
      monthlyLimit > 0 &&
      summariesUsed / monthlyLimit >= SUMMARY_LIMIT_APPROACHING_THRESHOLD;
    const showMetering = hasMeteringFeature && billingTier === 'free';

    return {
      summariesAllotted: monthlyLimit,
      summariesUsed,
      summarizationLimitApproaching,
      summarizationLimitReached,
      showMetering,
      reloadUsage: refetch,
      activeSummaryJob,
      summaryResponse: summaryResult,
      generateSummary,
      checkCachedSummary,
      deleteCachedSummary,
      registerSummaryPageMount,
    };
  }, [
    monthlyLimit,
    summariesUsed,
    refetch,
    activeSummaryJob,
    summaryResult,
    generateSummary,
    checkCachedSummary,
    deleteCachedSummary,
    registerSummaryPageMount,
    billingTier,
    hasMeteringFeature,
  ]);

  return (
    <UserPatientSummarizationContext.Provider value={summarizationState}>
      {isLoading ? <LoadingSpinner centered={true} /> : children}
    </UserPatientSummarizationContext.Provider>
  );
};

interface MockUserPatientSummarizationProviderProps extends PropsWithChildren {
  summariesAllotted: number;
  summariesUsed: number;
  activeSummaryJob?: ActiveSummaryJob | null;
  summaryResponse?: UsePatientSummariesResponse;
  showMetering?: boolean;
}

const MOCK_SUMMARY_RESPONSE: UsePatientSummariesResponse = {
  status: 'running',
  summaries: [],
  submitJob: () => undefined,
  reset: () => undefined,
  checkCachedSummary: async () => null,
  deleteSummaryJob: async () => false,
};

/** For use in stories and tests only. Provides static context values without API calls. */
export const MockUserPatientSummarizationProvider = ({
  summariesAllotted,
  summariesUsed,
  activeSummaryJob = null,
  summaryResponse = MOCK_SUMMARY_RESPONSE,
  showMetering = true,
  children,
}: MockUserPatientSummarizationProviderProps) => {
  const summarizationState = useMemo(() => {
    const summarizationLimitReached = summariesUsed >= summariesAllotted && summariesAllotted > 0;
    const summarizationLimitApproaching =
      !summarizationLimitReached &&
      summariesAllotted > 0 &&
      summariesUsed / summariesAllotted >= SUMMARY_LIMIT_APPROACHING_THRESHOLD;

    return {
      summariesAllotted,
      summariesUsed,
      summarizationLimitApproaching,
      summarizationLimitReached,
      showMetering,
      reloadUsage: () => Promise.resolve(),
      activeSummaryJob,
      summaryResponse,
      generateSummary: () => {
        /* noop */
      },
      checkCachedSummary: () => Promise.resolve(),
      deleteCachedSummary: () => Promise.resolve(false),
      registerSummaryPageMount: () => () => undefined,
    };
  }, [summariesAllotted, summariesUsed, activeSummaryJob, summaryResponse, showMetering]);

  return (
    <UserPatientSummarizationContext.Provider value={summarizationState}>
      {children}
    </UserPatientSummarizationContext.Provider>
  );
};

export const useUserPatientSummarizationContext = () => {
  const context = useContext(UserPatientSummarizationContext);

  if (!context) {
    throw new Error(
      'useUserPatientSummarizationContext must be used within a UserPatientSummarizationProvider',
    );
  }

  return context;
};
